<template>
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-4">
        <!-- Transform Selection - Left side (2/3 width) -->
        <div class="lg:col-span-2">
            <div class="space-y-6">
                <!-- Search Section -->
                <SearchInput placeholder="Search transforms..." custom-class="max-w-md" @search="transformSearchQuery = $event" />

                <!-- Transforms Grid -->
                <ScrollArea class="h-75-dynamic">
                    <div class="space-y-4 pr-4">
                        <!-- Skeleton loading state -->
                        <template v-if="loading">
                            <div v-for="n in 8" :key="`skeleton-${n}`" class="relative">
                                <Card class="transition-all">
                                    <CardContent class="flex items-center gap-3">
                                        <SkeletonLoader type="circle" class="h-10 w-10" :loading="true" />
                                        <div class="flex-1">
                                            <SkeletonLoader class="h-5 w-48" :loading="true" />
                                            <SkeletonLoader class="h-4 w-32 mt-1" :loading="true" />
                                        </div>
                                        <SkeletonLoader class="h-4 w-4" :loading="true" />
                                    </CardContent>
                                </Card>
                            </div>
                        </template>

                        <!-- Actual transform data -->
                        <template v-else>
                            <div v-for="(group, groupName) in filteredTransformGroups" :key="groupName" class="relative">
                                <!-- Single transform - direct selection -->
                                <Card
                                    v-if="group.length === 1"
                                    :class="[
                                        'group cursor-pointer transition-colors',
                                        group[0].coming_soon ? 'opacity-50 cursor-not-allowed bg-muted' : 'hover:bg-accent'
                                    ]"
                                    @click="!group[0].coming_soon && selectItem(group[0])">
                                    <CardContent class="flex items-center gap-3">
                                        <Avatar class="h-10 w-12 bg-background dark:bg-slate-200 border border-border rounded-md shrink-0">
                                            <AvatarImage :src="getTransformIcon(groupName)" :alt="groupName" class="object-contain p-1" />
                                            <AvatarFallback class="bg-background dark:bg-slate-200 dark:text-slate-800 text-muted-foreground text-xs rounded-md">
                                                {{ groupName.charAt(0).toUpperCase() }}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div class="flex-1 min-w-0">
                                            <div class="flex items-center gap-2">
                                                <h3 class="font-semibold group-hover:text-primary transition-colors">
                                                    {{ group[0].display_name || groupName }}
                                                </h3>
                                                <Badge
                                                    v-if="group[0].coming_soon"
                                                    variant="outline"
                                                    class="bg-background border-border text-foreground">
                                                    Coming Soon
                                                </Badge>
                                            </div>
                                            <p v-if="group[0].description" class="text-sm text-muted-foreground mt-1">
                                                {{ group[0].description }}
                                            </p>
                                        </div>
                                        <ChevronRight
                                            v-if="!group[0].coming_soon"
                                            class="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                                    </CardContent>
                                </Card>

                                <!-- Multiple transforms - expandable with inline children -->
                                <Collapsible v-else v-model:open="expandedGroups[groupName]">
                                    <CollapsibleTrigger asChild>
                                        <Card class="group cursor-pointer transition-colors hover:bg-accent">
                                            <CardContent class="flex items-center gap-3">
                                                <Avatar
                                                    class="h-10 w-12 bg-background dark:bg-slate-200 border border-border rounded-md shrink-0">
                                                    <AvatarImage :src="getTransformIcon(groupName)" :alt="groupName" class="object-contain p-1" />
                                                    <AvatarFallback
                                                        class="bg-background dark:bg-slate-200 dark:text-slate-800 text-muted-foreground text-xs rounded-md">
                                                        {{ groupName.charAt(0).toUpperCase() }}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div class="flex-1">
                                                    <h3 class="font-semibold group-hover:text-primary transition-colors">
                                                        {{ groupName }}
                                                    </h3>
                                                </div>
                                                <Badge variant="secondary" class="mr-2">
                                                    {{ group.length }}
                                                </Badge>
                                                <ChevronsUpDown
                                                    class="h-4 w-4 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
                                            </CardContent>
                                        </Card>
                                    </CollapsibleTrigger>

                                    <!-- Child cards - expandable content -->
                                    <CollapsibleContent class="ml-6 mt-3 space-y-2">
                                        <Card
                                            v-for="(child, index) in group"
                                            :key="index"
                                            :class="[
                                                'group cursor-pointer transition-colors',
                                                child.coming_soon ? 'opacity-50 cursor-not-allowed bg-muted' : 'hover:bg-accent/50'
                                            ]"
                                            @click="!child.coming_soon && selectItem(child)">
                                            <CardContent class="flex items-center gap-3">
                                                <Avatar
                                                    class="h-8 w-10 bg-background dark:bg-slate-200 border border-border rounded-md shrink-0">
                                                    <AvatarImage
                                                        :src="getTransformIcon(child.transform)"
                                                        :alt="child.transform"
                                                        class="object-contain p-0.5" />
                                                    <AvatarFallback
                                                        class="bg-background dark:bg-slate-200 dark:text-slate-800 text-muted-foreground text-xs rounded-md">
                                                        {{ child.transform.charAt(0).toUpperCase() }}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div class="flex-1 min-w-0">
                                                    <div class="flex items-center gap-2">
                                                        <p class="font-semibold text-sm group-hover:text-primary transition-colors">
                                                            {{ child.display_name }}
                                                        </p>
                                                        <Badge
                                                            v-if="child.coming_soon"
                                                            variant="outline"
                                                            class="text-xs bg-background border-border text-foreground">
                                                            Coming Soon
                                                        </Badge>
                                                    </div>
                                                    <p v-if="child.description" class="text-xs text-muted-foreground mt-1">
                                                        {{ child.description }}
                                                    </p>
                                                </div>
                                                <ChevronRight
                                                    v-if="!child.coming_soon"
                                                    class="h-3 w-3 text-muted-foreground group-hover:text-primary transition-colors" />
                                            </CardContent>
                                        </Card>
                                    </CollapsibleContent>
                                </Collapsible>
                            </div>

                            <!-- Empty state -->
                            <Card v-if="!filteredTransformGroups || !Object.keys(filteredTransformGroups).length" class="border-dashed">
                                <CardContent class="flex flex-col items-center justify-center p-6 text-center">
                                    <Cog class="h-8 w-8 text-muted-foreground mb-3" />
                                    <h3 class="font-medium mb-2">No transforms found</h3>
                                    <p class="text-sm text-muted-foreground mb-3">Try adjusting your search or check back later</p>
                                </CardContent>
                            </Card>
                        </template>
                    </div>
                    <ScrollBar orientation="vertical" />
                </ScrollArea>
            </div>
        </div>

        <!-- Information Panel - Right side (1/3 width) -->
        <div class="lg:col-span-1 mt-16">
            <Card class="sticky top-4 h-fit">
                <CardHeader>
                    <CardTitle class="flex items-center gap-2">
                        <Cog class="h-5 w-5" />
                        Transform Types
                    </CardTitle>
                </CardHeader>
                <CardContent class="space-y-4">
                    <div class="space-y-3">
                        <div class="p-3 bg-primary/5 rounded-lg">
                            <h4 class="font-medium text-sm mb-2">Data Processing</h4>
                            <p class="text-xs text-muted-foreground">Transform and manipulate your data as it flows through the pipeline.</p>
                        </div>

                        <div class="p-3 bg-primary/5 rounded-lg">
                            <h4 class="font-medium text-sm mb-2">Field Operations</h4>
                            <p class="text-xs text-muted-foreground">Modify, filter, or enrich individual fields in your data.</p>
                        </div>

                        <div class="p-3 bg-primary/5 rounded-lg">
                            <h4 class="font-medium text-sm mb-2">Data Quality</h4>
                            <p class="text-xs text-muted-foreground">Validate, clean, and ensure data quality standards.</p>
                        </div>
                    </div>

                    <div class="pt-4 border-t border-border">
                        <a
                            href="https://docs.streamkap.com/docs/transforms-1"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="flex items-center gap-2 text-sm text-primary hover:underline">
                            Learn more about transforms
                            <ExternalLink class="h-3 w-3" />
                        </a>
                    </div>
                </CardContent>
            </Card>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { getTransformIcon } from '@/utils/constants';
import SearchInput from '@/components/global/SearchInput.vue';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { SkeletonLoader } from '@/components/ui/skeleton-loader';
import { ChevronRight, ChevronsUpDown, Cog, ExternalLink } from 'lucide-vue-next';

const props = defineProps({
    transformGroups: {
        type: Object,
        required: true
    },
    loading: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['select-transform']);

const transformSearchQuery = ref('');
const expandedGroups = ref({});

const filteredTransformGroups = computed(() => {
    if (transformSearchQuery.value) {
        const q = transformSearchQuery.value.toLowerCase();
        return Object.keys(props.transformGroups).reduce((acc, groupName) => {
            const filteredGroup = props.transformGroups[groupName].filter(
                (x) =>
                    x.display_name?.toLowerCase().includes(q) ||
                    (x.description && x.description.toLowerCase().includes(q)) ||
                    groupName.toLowerCase().includes(q)
            );
            if (filteredGroup.length) {
                acc[groupName] = filteredGroup;
            }
            return acc;
        }, {});
    }

    return props.transformGroups;
});

const selectItem = (child) => {
    emit('select-transform', child);
};
</script>
